import { Component, OnInit, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { environment } from '@environments/environment';
import { IMenuItem } from '@app/shared/interfaces/menu.interface';
import { AuthService } from '@services/auth/auth.service';
import { UserDefinitionService } from '@services/user-definition.service';
import { LayoutService } from '@app/layout/service/layout.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { IUser } from '@shared/interfaces/user-definitions.interface';
import { filter } from 'rxjs/operators';

const SUPPLIER_ADMIN_ALLOWED_ROUTES = ['/', '/flights', '/reservations'];

@Component({
    selector: 'app-side-menu',
    imports: [CommonModule, RouterModule],
    templateUrl: './side-menu.component.html',
    styleUrl: './side-menu.component.scss'
})
export class SideMenuComponent implements OnInit {
    private router = inject(Router);
    private authService = inject(AuthService);
    private userDefinitionService = inject(UserDefinitionService);
    private layoutService = inject(LayoutService);

    items: IMenuItem[] = [];
    isSupplierAdmin = this.userDefinitionService.isSupplierAdmin;
    logoPath = environment.sideMenuLogo;
    logoText = environment.companyName;
    isMobileMenuOpen = signal(false);
    isCollapsed = signal(false);

    readonly userInfo = toSignal<IUser | null>(this.userDefinitionService.$userInfo.asObservable(), { initialValue: null });

    readonly userInitials = computed(() => {
        const name = this.userInfo()?.name || this.userInfo()?.userName || '';
        return name.replace(/[^A-Za-z ]/g, '').split(/\s+/).filter(Boolean).slice(0, 2).map((x: string) => x[0]).join('').toUpperCase() || '?';
    });

    readonly menuGroups = computed(() => {
        const groups: { label: string; items: IMenuItem[] }[] = [];
        const groupMap = new Map<string, IMenuItem[]>();

        for (const item of this.items) {
            const groupName = item.group || 'General';
            if (!groupMap.has(groupName)) {
                groupMap.set(groupName, []);
            }
            groupMap.get(groupName)!.push(item);
        }

        groupMap.forEach((items, label) => {
            groups.push({ label, items });
        });

        return groups;
    });

    ngOnInit(): void {
        this.userDefinitionService.getUserInfo().subscribe(() => {
            this.initMenuItems();
            this.expandActiveMenu();
        });

        this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
            this.collapseAllMenus();
            this.expandActiveMenu();
        });
    }

    toggleSubmenu(item: IMenuItem): void {
        if (item.children && item.children.length > 0) {
            const isExpanding = !item.expanded;
            this.collapseAllMenus();
            if (isExpanding) {
                item.expanded = true;
            }
        }
    }

    toggleMobileMenu(): void {
        this.isMobileMenuOpen.update((v) => !v);
    }

    closeMobileMenu(): void {
        this.isMobileMenuOpen.set(false);
    }

    toggleCollapse(): void {
        this.isCollapsed.update((v) => !v);
        this.layoutService.sidebarCollapsed.set(this.isCollapsed());
    }

    logout(): void {
        this.closeMobileMenu();
        this.authService.logout();
    }

    isActiveRoute(route: string | undefined): boolean {
        if (!route) return false;
        return this.router.url === route;
    }

    hasActiveChild(item: IMenuItem): boolean {
        if (!item.children) return false;
        return item.children.some((child) => this.isActiveRoute(child.route));
    }

    private initMenuItems(): void {
        const allItems: IMenuItem[] = [
            {
                label: 'Dashboard',
                icon: 'dashboard',
                route: '/',
                group: 'Overview'
            },
            {
                label: 'Reports',
                icon: 'analytics',
                group: 'Overview',
                children: [
                    { label: 'Quota & Risk Reports', route: '/reports/quota-risk-reports' }
                ],
                expanded: false,
                hide: !environment.showQuotaRiskReports
            },
            {
                label: 'Financial Reports',
                icon: 'assessment',
                group: 'Overview',
                children: [
                    { label: 'Revenue Comp. Detailed', route: '/reports/accounting-report' },
                    { label: 'Revenue Comp. General', route: '/reports/yearly-revenue-report' }
                ],
                expanded: false,
                hide: !environment.showReports
            },
            {
                label: 'Flights',
                icon: 'flight',
                route: '/flights',
                group: 'Operations'
            },
            {
                label: 'Reservations',
                icon: 'confirmation_number',
                route: '/reservations',
                group: 'Operations'
            },
            {
                label: 'Hotels',
                icon: 'hotel',
                group: 'Operations',
                children: [{ label: 'Hotel Booking List', route: '/hotels/hotel-booking-list' }],
                expanded: false,
                hide: !this.isHotelMenuEnabled()
            },
            {
                label: 'User & Access',
                icon: 'manage_accounts',
                group: 'Management',
                children: [
                    { label: 'Users Management', route: '/definitions/user-definitions' },
                    { label: 'Airline Rules Management', route: '/definitions/airline-rules-definitions' },
                    { label: 'APIS Rules Management', route: '/parameters/apis-definitions-management' }
                ],
                expanded: false
            },
            {
                label: 'Configuration',
                icon: 'tune',
                children: [{ label: 'Penalty Rule Definitions', route: '/configuration/penalty-rule-definitions' }],
                expanded: false
            },
            {
                label: 'Content',
                icon: 'auto_stories',
                route: '/cms',
                group: 'Management',
                hide: !environment.showLandingPage
            },
            {
                label: 'Products & Ancillaries',
                icon: 'settings_input_component',
                group: 'Management',
                children: [
                    { label: 'Product Management', route: '/parameters/product-management' },
                    { label: 'Product Pricing Management', route: '/parameters/additional-cost-management' },
                    { label: 'Fare bundles', route: '/parameters/ancillary-management' },
                    { label: 'Fare bundles Markups', route: '/parameters/ancillary-markup-management' },
                    { label: 'Coupons', route: '/parameters/discount-coupon', hide: !environment.showDiscountCoupon }
                ],
                expanded: false,
                hide: !environment.showParameters
            },
            {
                label: 'Payment Definitions',
                icon: 'credit_card',
                group: 'Management',
                children: [
                    { label: 'Tenant Definition', route: '/payment-definitions/tenant-definition' },
                    { label: 'Tenant User Definition', route: '/payment-definitions/tenant-user-definition' },
                    { label: 'PSP Definition', route: '/payment-definitions/psp-definition' },
                    { label: 'PSP Currency', route: '/payment-definitions/psp-currency' },
                    { label: 'PSP Setting', route: '/payment-definitions/psp-setting' },
                    { label: 'PSP Error Code', route: '/payment-definitions/psp-error-code' },
                    { label: 'Payment Transaction Log', route: '/payment-definitions/payment-transaction-log' }
                ],
                expanded: false
            }
        ];

        if (this.isSupplierAdmin()) {
            this.items = allItems.filter(
                (item) => item.route && SUPPLIER_ADMIN_ALLOWED_ROUTES.includes(item.route)
            );
        } else {
            this.items = allItems;
        }
    }

    private isHotelMenuEnabled(): boolean {
        try {
            return localStorage.getItem('hotel') === 'true';
        } catch {
            return false;
        }
    }

    private expandActiveMenu(): void {
        const currentUrl = this.router.url;
        this.items.forEach((item) => {
            if (item.children) {
                const hasActiveChild = item.children.some((child) => child.route === currentUrl);
                if (hasActiveChild) {
                    item.expanded = true;
                }
            }
        });
    }

    private collapseAllMenus(): void {
        this.items.forEach((item) => {
            item.expanded = false;
        });
    }
}
